import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle2, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Common";
import { useSeo } from "@/lib/seo";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  useSeo({ title: "Reset your password | Launch to Income", description: "Set a new password for your Launch to Income account.", path: "/reset", noindex: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login", { replace: true }), 1400);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#FDFCFE] px-6" data-testid="reset-password-page">
      <div className="mb-8"><Logo height="h-9" /></div>
      <div className="w-full max-w-sm">
        {done ? (
          <div className="text-center">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-50"><CheckCircle2 size={28} className="text-emerald-500" /></span>
            <h1 className="mt-5 font-heading text-2xl font-semibold">Password updated</h1>
            <p className="mt-2 text-slate-600">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold tracking-tight">Choose a new password</h1>
            <p className="mt-2 text-sm text-slate-600">Pick something at least 8 characters with a letter and a number.</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  placeholder="New password"
                  className="w-full rounded-xl border border-brand-line bg-white py-3 pl-10 pr-4 text-sm focus:border-brand focus:outline-none" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-red-600">{error}</p>}
              <button data-testid="reset-submit-btn" type="submit" disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3.5 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-60">
                {busy ? <Loader2 size={16} className="animate-spin" /> : null} Update password {!busy && <ArrowRight size={15} />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
